import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, X, RefreshCw, Play, Pause, Search, Save, Wand2, Music, Activity, Zap, Shuffle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AIVibeGenerator — Super Admin Playlist Manager tab.
 *
 * Pipeline:
 *   1. Admin picks up to 3 seed tracks via autocomplete (cache-first).
 *   2. Picks intensity: chill | hype | experimental.
 *   3. Backend generates 50 recs via Claude Sonnet 4.6 + maps to Apple.
 *   4. Admin previews (15s clip), Swaps individual picks, Removes duds.
 *   5. Saves as a Master Playlist (curated_playlists) so it can be pushed
 *      to any venue via the existing "Assign Playlist" flow.
 */
const INTENSITIES = [
  { key: 'chill',        label: 'Chill',        hint: 'Low BPM · downtempo · mellow' },
  { key: 'hype',         label: 'Hype',         hint: 'High energy · remixes · club edits' },
  { key: 'experimental', label: 'Experimental', hint: '60% known · 40% rising stars' },
];

// Hype Engine v2 — Energy Mapping phases. Track membership comes from the
// backend `result.phases` (arrays of apple_id per phase). If the backend
// didn't return phases (older payload), we render everything under Peak.
const PHASES = [
  { key: 'intro',     label: 'Intro',     color: 'text-[#00F5FF]', hint: 'Warm up — lowest energy, gentle ramp' },
  { key: 'peak',      label: 'Peak Hour', color: 'text-[#FF007F]', hint: 'Highest energy + danceability — hold the floor' },
  { key: 'cool_down', label: 'Cool Down', color: 'text-[#9D00FF]', hint: 'Taper down for last call' },
];

const AIVibeGenerator = () => {
  const [seeds, setSeeds] = useState([null, null, null]);     // three slots
  const [intensity, setIntensity] = useState('hype');

  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);   // {tracks, seeds, intensity}
  const [swappingIdx, setSwappingIdx] = useState(null);

  const [playlistName, setPlaylistName] = useState('');
  const [saving, setSaving] = useState(false);

  // Reshuffle counter — bumped by the "Reshuffle by BPM" button. The
  // phaseGroups memo re-sorts by ascending BPM whenever this changes, so
  // manual removals/swaps don't undo the natural-flow ordering.
  const [reshuffleTick, setReshuffleTick] = useState(0);

  // Derive phase membership from the backend's `result.phases` maps of
  // apple_id → phase key. Tracks not in any phase (edge case) fall into
  // "peak". Sort each phase by ascending BPM (reshuffle behaviour).
  const phaseGroups = useMemo(() => {
    if (!result) return { intro: [], peak: [], cool_down: [] };
    const byPhase = { intro: [], peak: [], cool_down: [] };
    const phases = result.phases || {};
    const idToPhase = new Map();
    Object.entries(phases).forEach(([phaseKey, ids]) => {
      (ids || []).forEach((aid) => idToPhase.set(aid, phaseKey));
    });
    (result.tracks || []).forEach((t) => {
      const bucket = idToPhase.get(t.apple_id) || 'peak';
      if (byPhase[bucket]) byPhase[bucket].push(t);
    });
    // Ascending BPM within each phase — unknowns (0) sink to the top so
    // the admin sees them and can decide whether to keep or swap.
    const byBpm = (a, b) => (Number(a.bpm) || 0) - (Number(b.bpm) || 0);
    byPhase.intro.sort(byBpm);
    byPhase.peak.sort(byBpm);
    byPhase.cool_down.sort(byBpm);
    return byPhase;
  }, [result, reshuffleTick]);

  // Audio preview
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const togglePreview = (track) => {
    if (!track.preview_url) {
      toast.error('No preview available for this track');
      return;
    }
    if (playingId === track.apple_id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(track.preview_url);
    audio.volume = 0.7;
    audio.play().catch((err) => {
      console.warn('preview play failed:', err);
      toast.error('Preview blocked by browser');
    });
    audio.onended = () => setPlayingId(null);
    // Auto-stop after 15s per spec
    setTimeout(() => {
      try { audio.pause(); } catch (e) { /* ignore */ }
      setPlayingId((p) => (p === track.apple_id ? null : p));
    }, 15000);
    audioRef.current = audio;
    setPlayingId(track.apple_id);
  };

  const chosenIds = seeds.filter(Boolean).map((s) => s.id);

  const handleGenerate = async () => {
    if (chosenIds.length === 0) {
      toast.error('Pick at least one seed track');
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/generate-vibe`,
        { seed_apple_ids: chosenIds, intensity },
        { withCredentials: true, timeout: 90000 },
      );
      setResult(res.data);
      const matched = (res.data.tracks || []).filter((t) => t.matched).length;
      toast.success(`Generated ${res.data.tracks?.length || 0} tracks — ${matched} matched on Apple`);
      if (!playlistName) {
        setPlaylistName(`AI Vibe · ${intensity} · ${new Date().toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Generation failed';
      toast.error(`Generate failed: ${msg}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleRemove = (idx) => {
    if (!result) return;
    const updated = { ...result, tracks: result.tracks.filter((_, i) => i !== idx) };
    setResult(updated);
  };

  const handleSwap = async (idx) => {
    if (!result) return;
    setSwappingIdx(idx);
    try {
      const excludeIds = result.tracks
        .map((t) => t.apple_id)
        .filter(Boolean);
      const replace = result.tracks[idx];
      const res = await axios.post(
        `${API}/admin/vibe/swap`,
        {
          seed_apple_ids: chosenIds,
          intensity,
          exclude_apple_ids: excludeIds,
          replace_track: { name: replace.name, artist_name: replace.artist_name },
        },
        { withCredentials: true, timeout: 30000 },
      );
      const newTrack = res.data.track;
      const updated = { ...result, tracks: result.tracks.map((t, i) => (i === idx ? newTrack : t)) };
      setResult(updated);
      toast.success(`Swapped in ${newTrack.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Swap failed');
    } finally {
      setSwappingIdx(null);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    const name = playlistName.trim();
    if (!name) {
      toast.error('Playlist name is required');
      return;
    }
    const matchedTracks = result.tracks.filter((t) => t.matched && t.apple_id);
    if (matchedTracks.length === 0) {
      toast.error('No matched tracks to save');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/vibe/save`,
        {
          name,
          description: `AI-generated · seeds: ${result.seeds.map((s) => s.name).join(', ')} · intensity: ${intensity}`,
          tracks: matchedTracks,
          seeds: chosenIds,
          intensity,
        },
        { withCredentials: true },
      );
      toast.success(`Master Playlist "${name}" saved!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-vibe-generator">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #9D00FF 0%, #FF007F 100%)', boxShadow: '0 0 24px rgba(157,0,255,0.35)' }}
        >
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-white text-xl font-bold font-['Outfit']">AI Vibe Generator</h2>
          <p className="text-gray-400 text-xs">Pick up to 3 seeds → 50 curated tracks in seconds.</p>
        </div>
      </div>

      {/* Step 1 — Seed inputs */}
      <div
        className="rounded-2xl border p-5"
        style={{
          background: 'linear-gradient(180deg, rgba(157,0,255,0.04) 0%, transparent 100%)',
          borderColor: 'rgba(157,0,255,0.2)',
        }}
      >
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#c0a3ff] font-['JetBrains_Mono'] mb-3">
          Step 1 · Seed Tracks
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="vibe-seed-inputs">
          {[0, 1, 2].map((i) => (
            <SeedPicker
              key={`seed-${i}`}
              index={i}
              value={seeds[i]}
              onChange={(t) => {
                const next = [...seeds];
                next[i] = t;
                setSeeds(next);
              }}
            />
          ))}
        </div>
      </div>

      {/* Step 2 — Intensity */}
      <div
        className="rounded-2xl border p-5"
        style={{
          background: 'linear-gradient(180deg, rgba(0,245,255,0.04) 0%, transparent 100%)',
          borderColor: 'rgba(0,245,255,0.2)',
        }}
      >
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#7ff1ff] font-['JetBrains_Mono'] mb-3">
          Step 2 · Intensity
        </p>
        <div className="grid grid-cols-3 gap-2" data-testid="vibe-intensity-slider">
          {INTENSITIES.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setIntensity(opt.key)}
              className="p-4 rounded-xl border transition-all text-left hover:scale-[1.02]"
              style={{
                background: intensity === opt.key
                  ? 'linear-gradient(135deg, rgba(0,245,255,0.15), rgba(157,0,255,0.15))'
                  : 'rgba(255,255,255,0.02)',
                borderColor: intensity === opt.key ? 'rgba(0,245,255,0.5)' : 'rgba(255,255,255,0.08)',
                boxShadow: intensity === opt.key ? '0 0 20px rgba(0,245,255,0.15)' : 'none',
              }}
              data-testid={`vibe-intensity-${opt.key}`}
              data-active={intensity === opt.key}
            >
              <p className="text-white font-bold text-sm mb-0.5">{opt.label}</p>
              <p className="text-gray-500 text-[11px]">{opt.hint}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Generate button */}
      <div className="flex justify-center">
        <Button
          onClick={handleGenerate}
          disabled={generating || chosenIds.length === 0}
          className="h-12 px-8 rounded-full text-white font-bold text-sm shadow-lg"
          style={{
            background: 'linear-gradient(90deg, #9D00FF 0%, #FF007F 100%)',
            boxShadow: '0 8px 32px -8px rgba(157,0,255,0.6)',
          }}
          data-testid="vibe-generate-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading the vibe…</>
          ) : (
            <><Wand2 className="w-4 h-4 mr-2" /> Generate 50 Tracks</>
          )}
        </Button>
      </div>

      {/* Result grid */}
      {result && (
        <div className="space-y-4" data-testid="vibe-result-block">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-white font-bold text-sm font-['Outfit']">
                {result.tracks.length} tracks · {result.tracks.filter((t) => t.matched).length} matched on Apple
                {result.seed_bpm_avg ? (
                  <span className="ml-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[#00F5FF]">
                    Anchor {result.seed_bpm_avg} BPM · window {result.tempo_window?.low}-{result.tempo_window?.high}
                  </span>
                ) : null}
              </p>
              <p className="text-gray-500 text-xs">
                Preview 15s · Swap for an alternative · Remove any dud · then Save as Master
              </p>
            </div>
            {/* Save block */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => setReshuffleTick((t) => t + 1)}
                className="h-10 px-4 rounded-full font-semibold text-white bg-white/5 border border-white/10 hover:bg-white/10"
                data-testid="vibe-reshuffle-btn"
                title="Re-order kept tracks by ascending BPM within their phase"
              >
                <Shuffle className="w-4 h-4 mr-2" /> Reshuffle by BPM
              </Button>
              <Input
                value={playlistName}
                onChange={(e) => setPlaylistName(e.target.value)}
                placeholder="Master playlist name"
                className="bg-[#1A1A1A] border-white/10 h-10 w-64"
                data-testid="vibe-playlist-name"
              />
              <Button
                onClick={handleSave}
                disabled={saving}
                className="h-10 px-4 rounded-full font-bold text-black bg-[#39FF14] hover:bg-[#2FCD10]"
                data-testid="vibe-save-master-btn"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save as Master</>}
              </Button>
            </div>
          </div>

          {/* Phased track grid — Intro → Peak → Cool Down */}
          <div className="space-y-6" data-testid="vibe-tracks-grid">
            {PHASES.map((phase) => {
              const rows = phaseGroups[phase.key] || [];
              if (rows.length === 0) return null;
              return (
                <div key={phase.key} data-testid={`vibe-phase-${phase.key}`}>
                  <div className="flex items-baseline gap-3 mb-2 border-b border-white/5 pb-2">
                    <span className={`text-[10px] font-mono uppercase tracking-[0.25em] ${phase.color}`}>
                      {phase.label}
                    </span>
                    <span className="text-gray-500 text-[10px] font-mono">{rows.length} tracks · asc BPM</span>
                    <span className="text-gray-600 text-[10px]">— {phase.hint}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
                    {rows.map((t) => {
                      const globalIdx = result.tracks.findIndex((x) => x === t || x.apple_id === t.apple_id);
                      return (
                        <VibeTrackCard
                          key={`${t.apple_id || 'unmatched'}-${globalIdx}`}
                          track={t}
                          index={globalIdx}
                          isPlaying={playingId === t.apple_id}
                          onPreview={() => togglePreview(t)}
                          onSwap={() => handleSwap(globalIdx)}
                          onRemove={() => handleRemove(globalIdx)}
                          swapping={swappingIdx === globalIdx}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Skipped log — tempo-mismatch rejects surfaced so the admin
              can see the BPM anchor working. Cache Shield note: skipped
              tracks are still fetched from ReccoBeats ONCE and cached, so
              the next generation with the same seeds is free. */}
          {result.skipped && result.skipped.length > 0 && (
            <div className="mt-6 rounded-xl border border-[#FF9500]/30 bg-[#FF9500]/[0.05] p-4" data-testid="vibe-skipped-log">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-[#FF9500]" />
                <p className="text-[#FF9500] text-[10px] font-mono uppercase tracking-[0.25em]">
                  Skipped · Tempo Mismatch · {result.skipped.length} track{result.skipped.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {result.skipped.map((s) => (
                  <div
                    key={`skipped-${s.apple_id}`}
                    className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/5 px-3 py-2"
                    data-testid={`vibe-skipped-${s.apple_id}`}
                  >
                    {s.album_image ? (
                      <img src={s.album_image} alt="" className="w-8 h-8 rounded object-cover shrink-0 opacity-60" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-white/5 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-white/70 text-xs font-medium truncate line-through decoration-[#FF9500]/50">{s.name}</p>
                      <p className="text-gray-500 text-[10px] truncate">{s.artist_name}</p>
                    </div>
                    <span className="text-[#FF9500] text-[10px] font-mono font-bold shrink-0">
                      {Math.round(s.bpm || 0)} BPM
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


/* --------------------------------------------------------------------- */
/* SeedPicker — autocomplete search input for a single slot              */
/* --------------------------------------------------------------------- */
const SeedPicker = ({ index, value, onChange }) => {
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!q.trim() || value) {
      setSuggestions([]);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/vibe/autocomplete`, {
          params: { q: q.trim() },
          withCredentials: true,
        });
        setSuggestions(res.data.tracks || []);
        setOpen(true);
      } catch (err) {
        console.warn('autocomplete failed', err);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, value]);

  // Click-outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (value) {
    return (
      <div className="rounded-xl border border-[#00F5FF]/40 bg-[#00F5FF]/5 p-2 flex items-center gap-2" data-testid={`seed-${index}-chosen`}>
        {value.album_image ? (
          <img src={value.album_image} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
            <Music className="w-4 h-4 text-gray-500" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-white text-xs font-semibold truncate">{value.name}</p>
          <p className="text-gray-400 text-[11px] truncate">{value.artist_name}</p>
        </div>
        <button
          onClick={() => { onChange(null); setQ(''); }}
          className="text-gray-400 hover:text-white p-1"
          data-testid={`seed-${index}-clear`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative" data-testid={`seed-${index}-picker`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder={`Seed ${index + 1} — search a song`}
          className="bg-[#1A1A1A] border-white/10 pl-9 h-11 text-sm"
          data-testid={`seed-${index}-input`}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-500" />}
      </div>
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-full rounded-xl overflow-hidden border border-white/10 shadow-2xl max-h-72 overflow-y-auto"
          style={{ background: '#121018' }}
          data-testid={`seed-${index}-suggestions`}
        >
          {suggestions.map((s) => (
            <button
              key={s.id}
              onClick={() => { onChange(s); setQ(''); setOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors text-left"
              data-testid={`seed-${index}-suggestion-${s.id}`}
            >
              {s.album_image ? (
                <img src={s.album_image} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Music className="w-3.5 h-3.5 text-gray-500" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-white text-xs font-semibold truncate">{s.name}</p>
                <p className="text-gray-400 text-[11px] truncate">{s.artist_name}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


/* --------------------------------------------------------------------- */
/* VibeTrackCard — one result card                                        */
/* --------------------------------------------------------------------- */
const VibeTrackCard = ({ track, index, isPlaying, onPreview, onSwap, onRemove, swapping }) => {
  const dim = !track.matched;
  const bpm = Number(track.bpm) || 0;
  const energy = Number(track.energy) || 0; // 0..1
  // Colour the energy dot low → high: gray (unknown) < cyan (low) <
  // yellow (mid) < magenta (high). Threshold at 0.4 / 0.7.
  let energyColor = '#4b5563';       // gray-600 — unknown
  let energyLabel = 'unknown';
  if (energy > 0.7)      { energyColor = '#FF007F'; energyLabel = 'high'; }
  else if (energy > 0.4) { energyColor = '#FFCC00'; energyLabel = 'mid';  }
  else if (energy > 0)   { energyColor = '#00F5FF'; energyLabel = 'low';  }

  return (
    <div
      className="rounded-xl bg-white/[0.03] border border-white/8 overflow-hidden group transition-all hover:border-white/20"
      style={dim ? { opacity: 0.4 } : undefined}
      data-testid={`vibe-track-${index}`}
      data-matched={String(!dim)}
    >
      <div className="relative aspect-square">
        {track.album_image ? (
          <img src={track.album_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#9D00FF]/25 to-[#FF007F]/25 flex items-center justify-center">
            <Music className="w-10 h-10 text-white/30" />
          </div>
        )}

        {/* BPM badge — top-left. Only shown when tempo is known. */}
        {bpm > 0 && (
          <div
            className="absolute top-1.5 left-1.5 bg-black/70 backdrop-blur-sm border border-white/10 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 font-mono"
            data-testid={`vibe-track-${index}-bpm`}
            title={`${Math.round(bpm)} BPM · ${energyLabel} energy`}
          >
            <Activity className="w-2.5 h-2.5 text-[#00F5FF]" strokeWidth={3} />
            {Math.round(bpm)}
          </div>
        )}

        {/* Energy dot — top-right corner, colored by energy tier. */}
        <div
          className="absolute top-1.5 right-1.5 flex items-center gap-1 bg-black/70 backdrop-blur-sm border border-white/10 px-1.5 py-0.5 rounded-full"
          data-testid={`vibe-track-${index}-energy`}
          title={`Energy: ${energyLabel}`}
        >
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: energyColor, boxShadow: energy > 0.7 ? `0 0 6px ${energyColor}` : 'none' }}
          />
          <Zap className="w-2.5 h-2.5 text-white/80" strokeWidth={3} />
        </div>

        {/* Preview / Swap / Remove overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {track.matched && track.preview_url && (
            <button
              onClick={onPreview}
              className="w-10 h-10 rounded-full bg-white/95 text-black hover:bg-white flex items-center justify-center transition-transform hover:scale-110"
              title="15-sec preview"
              data-testid={`vibe-track-${index}-preview`}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
            </button>
          )}
          {track.matched && (
            <button
              onClick={onSwap}
              disabled={swapping}
              className="w-10 h-10 rounded-full bg-[#00F5FF]/20 border border-[#00F5FF]/50 text-[#7ff1ff] hover:bg-[#00F5FF]/30 flex items-center justify-center"
              title="Swap for an alternative"
              data-testid={`vibe-track-${index}-swap`}
            >
              {swapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={onRemove}
            className="w-10 h-10 rounded-full bg-[#FF0055]/20 border border-[#FF0055]/50 text-[#ff7a9b] hover:bg-[#FF0055]/30 flex items-center justify-center"
            title="Remove"
            data-testid={`vibe-track-${index}-remove`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isPlaying && (
          <div className="absolute bottom-1.5 left-1.5 bg-[#00F5FF] text-black text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest">
            Preview
          </div>
        )}
        {dim && (
          <div className="absolute bottom-1.5 right-1.5 bg-[#FF0055]/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest">
            Not on Apple
          </div>
        )}
      </div>
      <div className="p-2">
        <p className="text-white text-xs font-semibold truncate">{track.name || track.llm_title}</p>
        <p className="text-gray-500 text-[11px] truncate">{track.artist_name || track.llm_artist}</p>
      </div>
    </div>
  );
};


export default AIVibeGenerator;
